import ClearableInput from '@rio-cloud/rio-uikit/components/clearableInput/ClearableInput';
import NumberInput from '@rio-cloud/rio-uikit/components/numberInput/NumberInput';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import classNames from 'classnames';
import {
    type ClipboardEvent,
    forwardRef,
    type InputHTMLAttributes,
    type KeyboardEvent,
    type ReactElement,
} from 'react';
import { type Control, useController } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { normalizeDecimalNumberInput, normalizeIntegerNumberInput } from '#utils/numberInputParsing.ts';
import type { Override } from '#utils/util.ts';

import { ControlSize } from './ControlSize.ts';
import { ValidationViolation } from './ValidationViolation.tsx';

type InputProps<Element = InputHTMLAttributes<HTMLInputElement>> = Element & {
    label: string;
    error?: string | string[] | false;
    // biome-ignore lint/suspicious/noExplicitAny: <react-hook-form> types
    control?: Control<any>;
    iconName?: string;
    className?: string;
    size?: ControlSize;
    name: string;
    action?: ReactElement;
    formGroupClassName?: string;
    addon?: string;
    flavor?: 'default' | 'error';
};

type FormInputProps = Override<
    Omit<InputProps, 'defaultValue' | 'type' | 'children'>,
    { value?: string | undefined; onChange?: (value: string) => void }
>;

type NumberInputProps = Override<
    InputProps,
    {
        value?: number;
        max?: number;
        min?: number;
        step?: number;
        numericMode?: 'integer' | 'decimal';
        inputContainerClassName?: string;
        accessibleLabel?: string;
        bsSize?: 'sm' | 'md' | 'lg';
    }
>;

const decimalSeparatorKeys = ['.', ','];
const defaultDecimalStep = 0.01;

const setNativeInputValue = (input: HTMLInputElement, value: string) => {
    const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    valueSetter?.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
};

const insertNativeInputText = (input: HTMLInputElement, value: string) => {
    try {
        input.setRangeText(value, input.selectionStart ?? 0, input.selectionEnd ?? 0, 'end');
        input.dispatchEvent(new Event('input', { bubbles: true }));
    } catch {
        setNativeInputValue(input, value);
    }
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
    ({ label, error, name, value, iconName, size, action, formGroupClassName, addon, ...props }, ref) => {
        const hasViolation = !!error;
        return (
            <div
                className={classNames('form-group', formGroupClassName, {
                    'form-group-lg': size === ControlSize.LARGE,
                    'has-error': hasViolation,
                    'has-feedback': hasViolation,
                })}
            >
                <FormLabel htmlFor={name} supportingText={action}>
                    {label}
                </FormLabel>

                <div className={'input-group'}>
                    {iconName && (
                        <span className='input-group-addon'>
                            <span className={`uikit-icon icon-${iconName}`} />
                        </span>
                    )}

                    <ClearableInput
                        {...props}
                        id={name}
                        name={name}
                        value={value}
                        onChange={(value: string) => props.onChange?.(value)}
                        ref={ref}
                    />
                    {addon && <span className='input-group-addon'>{addon}</span>}
                </div>
                <ValidationViolation violations={error} />
            </div>
        );
    }
);

export const ControlledFormInput = forwardRef<HTMLInputElement, FormInputProps>(({ name, control, ...props }, ref) => {
    const { field, fieldState } = useController({ control, name: name });
    return <FormInput error={fieldState.error?.message} {...field} {...props} ref={ref} />;
});

export const ControlledNumberFormInput = forwardRef<HTMLInputElement, NumberInputProps>(
    (
        {
            label,
            name,
            control,
            formGroupClassName,
            size,
            addon,
            action,
            numericMode = 'decimal',
            onKeyDown,
            inputContainerClassName,
            accessibleLabel,
            ...props
        },
        ref
    ) => {
        const { field, fieldState } = useController({ control, name: name });
        const intl = useIntl();
        const isIntegerMode = numericMode === 'integer';

        const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (isIntegerMode && decimalSeparatorKeys.includes(event.key)) {
                event.preventDefault();
            }
            onKeyDown?.(event);
        };

        const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
            const pastedValue = event.clipboardData.getData('text');
            event.preventDefault();
            const normalizedValue = isIntegerMode
                ? normalizeIntegerNumberInput(pastedValue, intl.locale)
                : normalizeDecimalNumberInput(pastedValue, intl.locale);
            if (normalizedValue !== undefined) {
                insertNativeInputText(event.currentTarget, normalizedValue);
            }
        };

        return (
            <div
                className={classNames('form-group', formGroupClassName, {
                    'form-group-lg': size === ControlSize.LARGE,
                    'has-error': fieldState.invalid,
                    'has-feedback': fieldState.invalid,
                })}
            >
                <FormLabel htmlFor={field.name} supportingText={action && action}>
                    {label}
                </FormLabel>
                <div className={inputContainerClassName}>
                    {accessibleLabel && (
                        <span id={`${field.name}-accessible-label`} hidden>
                            {accessibleLabel}
                        </span>
                    )}
                    <NumberInput
                        {...props}
                        {...field}
                        aria-labelledby={accessibleLabel ? `${field.name}-accessible-label` : undefined}
                        unit={addon}
                        id={field.name}
                        noDefault={true}
                        step={isIntegerMode ? 1 : (props.step ?? defaultDecimalStep)}
                        inputMode={isIntegerMode ? 'numeric' : props.inputMode}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        ref={ref}
                    />
                </div>
                <ValidationViolation violations={fieldState.error?.message} />
            </div>
        );
    }
);
